/**
 * Theme presets — drive the storefront colour palette + typography by
 * overriding the same CSS custom properties already used across the app
 * (--kb-primary, --kb-accent, --kb-bg, etc.). Switching `theme_preset`
 * on /admin/branding instantly re-skins every customer's experience.
 *
 * Each preset uses HSL triplets (no alpha) so existing `hsl(var(--kb-primary) / 0.06)`
 * patterns continue to work.
 *
 * `fontPairs` — curated typography pairings hand-picked for each preset.
 * The tenant picks one (or sticks with the default). Each pair declares:
 *   key      — stable identifier persisted on settings.font_pair
 *   label    — short headline shown in the picker card
 *   note     — one-liner about when this pair shines
 *   display  — value for the `font-display` class (headings, logo)
 *   body     — value for the document <body>
 *   googleFamilies — array of Google Fonts query strings to lazy-load
 */

const PAIR = (key, label, note, display, body, googleFamilies) => ({
  key, label, note, display, body, googleFamilies,
});

export const THEME_PRESETS = {
  kalki: {
    label: "Kalki — Maroon & Gold",
    description: "Editorial Indian luxury. Deep maroon, soft pearl, golden accents.",
    swatches: ["#7A1C29", "#D4AF37", "#FDFBF7"],
    vars: {
      "--kb-bg": "36 53% 98%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "38 29% 94%",
      "--kb-primary": "351 62% 29%",
      "--kb-primary-hover": "351 62% 22%",
      "--kb-text": "0 0% 10%",
      "--kb-muted": "0 0% 40%",
      "--kb-border": "35 21% 87%",
      "--kb-accent": "46 65% 52%",
      "--kb-dashboard-bg": "40 15% 97%",
    },
    fontPairs: [
      PAIR("editorial", "Editorial Luxe", "Soft serif headings paired with a clean modern sans. Our signature recommendation.",
        "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
        "'Outfit', system-ui, sans-serif",
        ["Cormorant+Garamond:wght@300;400;500;600;700", "Outfit:wght@300;400;500;600;700"]),
      PAIR("regal", "Regal Boutique", "Higher contrast Didone headings. Feels couture-store.",
        "'Playfair Display', 'Bodoni Moda', Georgia, serif",
        "'Lora', Georgia, serif",
        ["Playfair+Display:wght@400;500;600;700;800", "Lora:wght@400;500;600;700"]),
      PAIR("heritage", "Heritage", "Sharp serif for tradition, warm humanist sans for clarity.",
        "'DM Serif Display', 'Cormorant Garamond', serif",
        "'Work Sans', system-ui, sans-serif",
        ["DM+Serif+Display", "Work+Sans:wght@300;400;500;600"]),
      PAIR("modern-luxe", "Modern Luxe", "Geometric headings; clean reads for younger luxury buyers.",
        "'Cormorant Infant', 'Cormorant Garamond', serif",
        "'Manrope', system-ui, sans-serif",
        ["Cormorant+Infant:wght@400;500;600;700", "Manrope:wght@300;400;500;600;700"]),
    ],
    radius: "0.25rem",
  },

  myntra: {
    label: "Myntra — Magenta & Tangerine",
    description: "Bright, energetic e-commerce. Pink-magenta primary, orange accents, dense grid.",
    swatches: ["#FF3F6C", "#FF905A", "#FFFFFF"],
    vars: {
      "--kb-bg": "0 0% 100%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 97%",
      "--kb-primary": "344 100% 62%",
      "--kb-primary-hover": "344 100% 52%",
      "--kb-text": "210 11% 15%",
      "--kb-muted": "210 7% 46%",
      "--kb-border": "0 0% 89%",
      "--kb-accent": "20 100% 60%",
      "--kb-dashboard-bg": "0 0% 99%",
    },
    fontPairs: [
      PAIR("commerce", "Commerce", "Friendly Poppins headlines, ultra-legible Inter body. Marketplace default.",
        "'Poppins', sans-serif", "'Inter', system-ui, sans-serif",
        ["Poppins:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("trendy", "Trendy", "Compact Plus Jakarta Sans for headers, paired with Inter.",
        "'Plus Jakarta Sans', sans-serif", "'Inter', system-ui, sans-serif",
        ["Plus+Jakarta+Sans:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("fresh", "Fresh", "Soft Manrope across everything — keeps the page calm.",
        "'Manrope', sans-serif", "'Manrope', system-ui, sans-serif",
        ["Manrope:wght@300;400;500;600;700;800"]),
      PAIR("playful", "Playful Editorial", "Plump DM Sans headlines balance the bright pink palette.",
        "'DM Sans', sans-serif", "'Inter', system-ui, sans-serif",
        ["DM+Sans:wght@400;500;700", "Inter:wght@300;400;500;600;700"]),
    ],
    radius: "0.5rem",
  },

  ajio: {
    label: "Ajio — Charcoal & Acid Lime",
    description: "Sporty modern. Near-black primary, neon-lime accent, geometric.",
    swatches: ["#171717", "#D6FF00", "#FAFAFA"],
    vars: {
      "--kb-bg": "0 0% 99%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 95%",
      "--kb-primary": "0 0% 9%",
      "--kb-primary-hover": "0 0% 0%",
      "--kb-text": "0 0% 9%",
      "--kb-muted": "0 0% 45%",
      "--kb-border": "0 0% 88%",
      "--kb-accent": "70 100% 50%",
      "--kb-dashboard-bg": "0 0% 98%",
    },
    fontPairs: [
      PAIR("athletic", "Athletic", "Manrope everywhere — clean, geometric, sporty.",
        "'Manrope', sans-serif", "'Manrope', system-ui, sans-serif",
        ["Manrope:wght@300;400;500;600;700;800"]),
      PAIR("brutalist", "Brutalist", "Sharp Space Grotesk headers over neutral Inter body.",
        "'Space Grotesk', sans-serif", "'Inter', system-ui, sans-serif",
        ["Space+Grotesk:wght@400;500;600;700", "Inter:wght@300;400;500;600;700"]),
      PAIR("technical", "Technical", "Wide, condensed Archivo for impact + clean reading text.",
        "'Archivo', sans-serif", "'Inter', system-ui, sans-serif",
        ["Archivo:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("hype", "Hype Drop", "Heavy Anton headers; great for limited drops + flash sale banners.",
        "'Anton', 'Bebas Neue', sans-serif", "'Inter', system-ui, sans-serif",
        ["Anton", "Inter:wght@300;400;500;600;700"]),
    ],
    radius: "0.125rem",
  },

  mango: {
    label: "Mango — Pure Monochrome",
    description: "Editorial monochrome. Black on white minimalism, generous whitespace, large serif type.",
    swatches: ["#000000", "#B0B0B0", "#FFFFFF"],
    vars: {
      "--kb-bg": "0 0% 100%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 96%",
      "--kb-primary": "0 0% 0%",
      "--kb-primary-hover": "0 0% 22%",
      "--kb-text": "0 0% 7%",
      "--kb-muted": "0 0% 45%",
      "--kb-border": "0 0% 88%",
      "--kb-accent": "0 0% 60%",
      "--kb-dashboard-bg": "0 0% 99%",
    },
    fontPairs: [
      PAIR("classic", "Classic Editorial", "Magazine-style Playfair display with Inter body.",
        "'Playfair Display', serif", "'Inter', system-ui, sans-serif",
        ["Playfair+Display:wght@400;500;600;700;800", "Inter:wght@300;400;500;600;700"]),
      PAIR("monoluxe", "Mono Luxe", "Tall Bodoni headlines balanced by a humanist sans.",
        "'Bodoni Moda', serif", "'Karla', system-ui, sans-serif",
        ["Bodoni+Moda:wght@400;500;600;700;800", "Karla:wght@300;400;500;600;700"]),
      PAIR("salon", "Salon", "Stylish Cormorant headings with calm Manrope reading.",
        "'Cormorant Garamond', serif", "'Manrope', system-ui, sans-serif",
        ["Cormorant+Garamond:wght@300;400;500;600;700", "Manrope:wght@300;400;500;600;700"]),
      PAIR("museum", "Museum", "Big-presence Fraunces headings with quiet Inter body.",
        "'Fraunces', serif", "'Inter', system-ui, sans-serif",
        ["Fraunces:wght@300;400;500;600;700;900", "Inter:wght@300;400;500;600;700"]),
    ],
    radius: "0",
  },

  biba: {
    label: "Biba — Coral & Cream",
    description: "Warm Indian ethnic. Coral-red primary, cream backdrop, ochre accent.",
    swatches: ["#C8473C", "#E5A33B", "#FBF4E9"],
    vars: {
      "--kb-bg": "33 67% 96%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "33 47% 90%",
      "--kb-primary": "5 60% 51%",
      "--kb-primary-hover": "5 60% 42%",
      "--kb-text": "20 14% 16%",
      "--kb-muted": "20 8% 42%",
      "--kb-border": "33 32% 84%",
      "--kb-accent": "37 73% 56%",
      "--kb-dashboard-bg": "33 50% 95%",
    },
    fontPairs: [
      PAIR("warm-classic", "Warm Classic", "Tinos heading with Lora body — book-paper warmth.",
        "'Tinos', serif", "'Lora', Georgia, serif",
        ["Tinos:wght@400;700", "Lora:wght@400;500;600;700"]),
      PAIR("market", "Market Bazaar", "Friendly Crimson Pro headers, soft Source Sans body.",
        "'Crimson Pro', serif", "'Source Sans 3', system-ui, sans-serif",
        ["Crimson+Pro:wght@400;500;600;700", "Source+Sans+3:wght@300;400;500;600"]),
      PAIR("festive", "Festive", "Decorative Frank Ruhl Libre headlines + clean Mulish reading.",
        "'Frank Ruhl Libre', serif", "'Mulish', system-ui, sans-serif",
        ["Frank+Ruhl+Libre:wght@400;500;700;900", "Mulish:wght@300;400;500;600;700"]),
      PAIR("artisan", "Artisan", "Hand-lettered feel Italiana headings + Lora body.",
        "'Italiana', serif", "'Lora', Georgia, serif",
        ["Italiana", "Lora:wght@400;500;600;700"]),
    ],
    radius: "0.25rem",
  },

  neerus: {
    label: "Neerus — Emerald & Champagne",
    description: "Couture jewel. Deep emerald primary, champagne gold accent, ornate type.",
    swatches: ["#19543E", "#C9A86B", "#FAF7EE"],
    vars: {
      "--kb-bg": "50 41% 96%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "50 30% 92%",
      "--kb-primary": "156 53% 22%",
      "--kb-primary-hover": "156 53% 16%",
      "--kb-text": "165 22% 12%",
      "--kb-muted": "165 8% 38%",
      "--kb-border": "50 18% 82%",
      "--kb-accent": "39 41% 60%",
      "--kb-dashboard-bg": "50 35% 95%",
    },
    fontPairs: [
      PAIR("couture", "Couture", "All-caps Cinzel headers, soft Cormorant Garamond body.",
        "'Cinzel', serif", "'Cormorant Garamond', Georgia, serif",
        ["Cinzel:wght@400;500;600;700", "Cormorant+Garamond:wght@300;400;500;600;700"]),
      PAIR("opera", "Opera", "Tall Cormorant SC headings paired with refined Cormorant body.",
        "'Cormorant SC', serif", "'Cormorant Garamond', Georgia, serif",
        ["Cormorant+SC:wght@400;500;600;700", "Cormorant+Garamond:wght@300;400;500;600;700"]),
      PAIR("regalia", "Regalia", "Elegant Marcellus titles balanced by warm Lora body.",
        "'Marcellus', serif", "'Lora', Georgia, serif",
        ["Marcellus", "Lora:wght@400;500;600;700"]),
      PAIR("haute", "Haute Couture", "Higher contrast Cinzel + Crimson Pro for editorial reads.",
        "'Cinzel', serif", "'Crimson Pro', Georgia, serif",
        ["Cinzel:wght@400;500;600;700", "Crimson+Pro:wght@400;500;600;700"]),
    ],
    radius: "0.125rem",
  },

  flipkart: {
    label: "Flipkart — Sky Blue & Yellow",
    description: "Marketplace classic — Flipkart blue primary, sunshine yellow accent, soft sky tint.",
    swatches: ["#2874F0", "#FFE500", "#F0F7FF"],
    vars: {
      "--kb-bg": "210 100% 97%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "210 60% 95%",
      "--kb-primary": "215 91% 55%",
      "--kb-primary-hover": "215 91% 47%",
      "--kb-text": "215 28% 17%",
      "--kb-muted": "215 14% 45%",
      "--kb-border": "210 30% 88%",
      "--kb-accent": "52 100% 50%",
      "--kb-dashboard-bg": "210 50% 97%",
    },
    fontPairs: [
      PAIR("flipkart-default", "Marketplace", "The signature Flipkart look — friendly Roboto, ultra-legible Inter body.",
        "'Roboto', system-ui, sans-serif", "'Inter', system-ui, sans-serif",
        ["Roboto:wght@400;500;700;900", "Inter:wght@400;500;600;700"]),
      PAIR("flipkart-modern", "Bold Sale", "Heavy DM Sans for sale headlines, paired with airy Inter.",
        "'DM Sans', system-ui, sans-serif", "'Inter', system-ui, sans-serif",
        ["DM+Sans:wght@500;700;900", "Inter:wght@400;500;600"]),
      PAIR("flipkart-snappy", "Snappy", "Compact Plus Jakarta Sans for tight product grids.",
        "'Plus Jakarta Sans', sans-serif", "'Plus Jakarta Sans', system-ui, sans-serif",
        ["Plus+Jakarta+Sans:wght@400;500;600;700;800"]),
      PAIR("flipkart-friendly", "Friendly", "Manrope across the board — clean, mass-market.",
        "'Manrope', sans-serif", "'Manrope', system-ui, sans-serif",
        ["Manrope:wght@400;500;600;700;800"]),
    ],
    radius: "0.5rem",
  },

  amazon: {
    label: "Amazon Bazaar — Coral & Green",
    description: "Punchy deal-driven layout — coral red header, lime green category circles, yellow CTAs.",
    swatches: ["#FF5252", "#3DDC84", "#FFD814"],
    vars: {
      "--kb-bg": "0 0% 100%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "0 0% 97%",
      "--kb-primary": "0 100% 65%",
      "--kb-primary-hover": "0 100% 57%",
      "--kb-text": "0 0% 13%",
      "--kb-muted": "0 0% 40%",
      "--kb-border": "0 0% 88%",
      "--kb-accent": "47 100% 54%",
      "--kb-dashboard-bg": "0 0% 99%",
    },
    fontPairs: [
      PAIR("amazon-default", "Bazaar", "The Amazon Bazaar default — Amazon Ember-like Open Sans + Inter body.",
        "'Open Sans', sans-serif", "'Inter', system-ui, sans-serif",
        ["Open+Sans:wght@400;500;600;700;800", "Inter:wght@400;500;600"]),
      PAIR("amazon-bold", "Crazy Deals", "Big chunky Sora headings for sale energy.",
        "'Sora', sans-serif", "'Inter', system-ui, sans-serif",
        ["Sora:wght@500;700;800", "Inter:wght@400;500;600"]),
      PAIR("amazon-readable", "Readable", "Friendly Nunito across — easy on the eyes.",
        "'Nunito', sans-serif", "'Nunito', system-ui, sans-serif",
        ["Nunito:wght@400;500;600;700;800"]),
      PAIR("amazon-rounded", "Rounded", "Rounded Quicksand for a softer marketplace feel.",
        "'Quicksand', sans-serif", "'Inter', system-ui, sans-serif",
        ["Quicksand:wght@400;500;600;700", "Inter:wght@400;500;600"]),
    ],
    radius: "0.75rem",
  },

  asfashion: {
    label: "AS Fashion — Hot Pink & Charcoal",
    description: "Boutique ethnic e-commerce. Charcoal CTAs on ivory, hot-pink accents for prices/badges, editorial serif headings.",
    swatches: ["#111827", "#FF47B6", "#FAF8F6"],
    vars: {
      // Ivory-tinted white body, charcoal CTAs, hot-pink accent for
      // discounts / hover states / active slide indicators.
      "--kb-bg": "0 0% 100%",
      "--kb-surface": "0 0% 100%",
      "--kb-surface-alt": "24 27% 97%",     // #faf8f6 warm cream section band
      "--kb-primary": "222 47% 11%",         // #111827 gray-900 charcoal
      "--kb-primary-hover": "222 47% 20%",   // slightly lifted charcoal on hover
      "--kb-text": "222 47% 11%",
      "--kb-muted": "220 9% 46%",            // gray-500
      "--kb-border": "220 13% 91%",          // gray-200
      "--kb-accent": "325 100% 64%",         // #ff47b6 hot pink
      "--kb-dashboard-bg": "24 27% 97%",
    },
    fontPairs: [
      PAIR("boutique", "Boutique",
        "The AS Fashion signature — light Roboto Serif headings paired with clean Inter body. Editorial calm with pink accents.",
        "'Roboto Serif', 'DM Serif Text', serif", "'Inter', system-ui, sans-serif",
        ["Roboto+Serif:wght@200;300;400;500;600;700", "Inter:wght@300;400;500;600;700"]),
      PAIR("editorial-pink", "Editorial Pink",
        "Higher-contrast Playfair headers over Manrope reading.",
        "'Playfair Display', serif", "'Manrope', system-ui, sans-serif",
        ["Playfair+Display:wght@400;500;600;700", "Manrope:wght@300;400;500;600;700"]),
      PAIR("modern-atelier", "Modern Atelier",
        "Fraunces display for elegant sections + Inter body for tight product grids.",
        "'Fraunces', serif", "'Inter', system-ui, sans-serif",
        ["Fraunces:wght@300;400;500;600;700", "Inter:wght@300;400;500;600;700"]),
      PAIR("soft-serif", "Soft Serif",
        "Cormorant headings; Karla body — plush, feminine, calm.",
        "'Cormorant Garamond', serif", "'Karla', system-ui, sans-serif",
        ["Cormorant+Garamond:wght@300;400;500;600;700", "Karla:wght@300;400;500;600;700"]),
    ],
    radius: "0",
  },
};

export const DEFAULT_PRESET = "kalki";

export function getPreset(key) {
  return THEME_PRESETS[key] || THEME_PRESETS[DEFAULT_PRESET];
}

/**
 * Returns the font pair the tenant has selected for the active preset,
 * or the preset's first pair (= recommended default) when unset.
 */
export function getFontPair(presetKey, pairKey) {
  const preset = getPreset(presetKey);
  const pairs = preset.fontPairs || [];
  if (!pairs.length) return null;
  if (pairKey) {
    const match = pairs.find((p) => p.key === pairKey);
    if (match) return match;
  }
  return pairs[0];
}
